// HM-339+340+341+342 cluster 16.1: walk-in Step 1 dynamic SPEC mockup B
// (docs/design-references/walkin-step1-dynamic-mockup.html).
//
// Sub-componenti privati:
// - RoomInfoCard: banner camera con tipologia + listino + status LIBERA
// - DurationPresets: 4 card preset (2h/3h/4h/Pernotto) con price live
// - CustomDurationSection: counter +/- + TimePreview check-in -> check-out
// - PriceBreakdown: card gradient verde con breakdown calcolato
// - WarningBanner: weekend/holiday cap reached
//
// Calcolo prezzi: chiamata POST /pricing-rules/preview-calc per ogni
// preset (silent batched) + per il valore custom counter (debounced 200ms).
// Ricava context camera+listino da GET /rooms/:id/pricing-context.

import { CalendarClock, CircleCheck, Wallet } from '@hotel-manager/ui/icons';
import { cn } from '@hotel-manager/ui/lib/cn';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type PriceCalcResult,
  type PricingRule,
  fetchRoomPricingContext,
  previewCalc,
} from '../../api/pricing.js';
import { buildingLabelOf } from '../../lib/buildingLabel.js';
import { type WalkInMode, useWalkInWizard } from '../../stores/walkIn.js';

// Mappa la scelta dynamic alla mode legacy WalkInMode usata da Step 2/3/4
function deriveLegacyMode(selectedMode: 'hours' | 'overnight', hours: number): WalkInMode {
  if (selectedMode === 'overnight') return 'overnight';
  return hours <= 4 ? 'day_use_4h' : 'day_use_8h';
}

export function StepSelectRoom() {
  const room = useWalkInWizard((s) => s.room);
  if (!room) return null;
  return <ContextLoader roomId={room.id} />;
}

// --------------------------- ContextLoader ---------------------------

function ContextLoader({ roomId }: { roomId: string }) {
  const ctxQuery = useQuery({
    queryKey: ['room-pricing-context', roomId],
    queryFn: () => fetchRoomPricingContext(roomId),
    staleTime: 60_000,
  });

  if (ctxQuery.isLoading) {
    return <p className="text-text-secondary text-sm">Caricamento contesto camera…</p>;
  }
  if (ctxQuery.error || !ctxQuery.data) {
    return (
      <div className="rounded-md p-3 text-sm bg-state-guas/10 text-state-guas border border-state-guas/30">
        Errore caricamento camera: {(ctxQuery.error as Error | undefined)?.message ?? 'unknown'}
      </div>
    );
  }
  return <StepContent ctx={ctxQuery.data} />;
}

// --------------------------- StepContent ---------------------------

function StepContent({
  ctx,
}: {
  ctx: {
    room: { code: string; floor: string; building: string };
    type: { label: string };
    pricingRule: PricingRule | null;
  };
}) {
  const dynamicPricing = useWalkInWizard((s) => s.dynamicPricing);
  const setDynamicPricing = useWalkInWizard((s) => s.setDynamicPricing);
  const setMode = useWalkInWizard((s) => s.setMode);
  const preselectedStart = useWalkInWizard((s) => s.preselectedStart);

  // HM-412 cluster 17.2 polish: ora di check-in preselezionata da
  // Planning Gantt onSlotClick. Fallback a new Date() per ingressi
  // wizard senza preselection (es. apertura da dashboard RoomGrid).
  const checkInDate = useMemo(() => preselectedStart ?? new Date(), [preselectedStart]);
  const isoDate = useMemo(() => checkInDate.toISOString(), [checkInDate]);

  // Sync legacy mode quando dynamic cambia
  useEffect(() => {
    setMode(deriveLegacyMode(dynamicPricing.selectedMode, dynamicPricing.selectedHours));
  }, [dynamicPricing.selectedMode, dynamicPricing.selectedHours, setMode]);

  // Calcolo silente preset
  const presetHours = useMemo(() => [2, 3, 4] as const, []);
  const presetCalcs = usePresetCalcs(ctx.pricingRule, presetHours, isoDate);

  // Calcolo live current selection (debounced 200ms)
  const currentCalc = useCurrentCalc(ctx.pricingRule, dynamicPricing, isoDate);

  // Aggiorna store con priceCalc + warning derivati
  useEffect(() => {
    if (!currentCalc) return;
    setDynamicPricing({
      priceCalc: {
        total: currentCalc.total,
        breakdown: currentCalc.breakdown,
        appliedCap: currentCalc.appliedCap,
      },
      hasWeekendWarning: currentCalc.appliedCap === 'weekend' || currentCalc.isWeekend,
      hasHolidayWarning: currentCalc.appliedCap === 'holiday' || currentCalc.isHoliday,
    });
  }, [currentCalc, setDynamicPricing]);

  return (
    <div data-testid="step-select-room" className="space-y-6">
      <RoomInfoCard
        code={ctx.room.code}
        building={ctx.room.building}
        floor={ctx.room.floor}
        tipoLabel={ctx.type.label}
        listinoName={ctx.pricingRule?.name ?? '—'}
      />

      <SectionTitleRow title="Durata soggiorno" />

      <DurationPresets
        selected={
          dynamicPricing.selectedMode === 'overnight' ? 'overnight' : dynamicPricing.selectedHours
        }
        presetHours={presetHours}
        presetCalcs={presetCalcs}
        overnightPrice={Number(ctx.pricingRule?.overnightPrice ?? 0)}
        checkInDate={checkInDate}
        onSelect={(value) => {
          if (value === 'overnight') {
            setDynamicPricing({ selectedMode: 'overnight' });
          } else {
            setDynamicPricing({ selectedMode: 'hours', selectedHours: value });
          }
        }}
      />

      <CustomDurationSection
        hours={dynamicPricing.selectedHours}
        isOvernight={dynamicPricing.selectedMode === 'overnight'}
        bufferMinutes={ctx.pricingRule?.bufferMinutes ?? 20}
        checkInDate={checkInDate}
        onChange={(h) => setDynamicPricing({ selectedMode: 'hours', selectedHours: h })}
      />

      <PriceBreakdown listinoName={ctx.pricingRule?.name ?? '—'} calc={currentCalc} />

      {(currentCalc?.appliedCap === 'weekend' ||
        (currentCalc?.isWeekend && currentCalc?.appliedCap === 'overnight')) && (
        <WarningBanner
          title="Cap weekend raggiunto."
          message={`Nei sabati e domeniche il listino prevede massimo ${ctx.pricingRule?.weekendMaxHours ?? 3} ore. Per durate maggiori sara' applicato il pernottamento (€ ${ctx.pricingRule?.overnightPrice ?? '—'}).`}
        />
      )}
      {currentCalc?.isHoliday && (
        <WarningBanner
          title="Festivo."
          message={`Oggi e' festivo: maggiorazione +${ctx.pricingRule?.holidaySurchargePct ?? 30}% applicata sul totale.`}
        />
      )}

      <StepFooter />
    </div>
  );
}

// --------------------------- Hooks calcolo ---------------------------

function usePresetCalcs(
  rule: PricingRule | null,
  hours: readonly number[],
  isoDate: string,
): Map<number, PriceCalcResult> {
  const [results, setResults] = useState<Map<number, PriceCalcResult>>(new Map());

  useEffect(() => {
    if (!rule) return;
    let cancelled = false;
    Promise.all(
      hours.map((h) =>
        previewCalc({
          rule: ruleToPreviewBody(rule),
          hours: h,
          date: isoDate,
        }).then((r) => [h, r] as const),
      ),
    )
      .then((pairs) => {
        if (cancelled) return;
        setResults(new Map(pairs));
      })
      .catch(() => {
        if (cancelled) return;
        setResults(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [rule, hours, isoDate]);

  return results;
}

function useCurrentCalc(
  rule: PricingRule | null,
  dyn: { selectedMode: 'hours' | 'overnight'; selectedHours: number },
  isoDate: string,
): (PriceCalcResult & { isHoliday: boolean; isWeekend: boolean }) | null {
  const [calc, setCalc] = useState<
    (PriceCalcResult & { isHoliday: boolean; isWeekend: boolean }) | null
  >(null);

  useEffect(() => {
    if (!rule) return;
    const t = setTimeout(() => {
      const hours = dyn.selectedMode === 'overnight' ? 24 : dyn.selectedHours;
      previewCalc({
        rule: ruleToPreviewBody(rule),
        hours,
        date: isoDate,
      })
        .then((r) => setCalc(r))
        .catch(() => setCalc(null));
    }, 200);
    return () => clearTimeout(t);
  }, [rule, dyn.selectedMode, dyn.selectedHours, isoDate]);

  return calc;
}

function ruleToPreviewBody(r: PricingRule) {
  return {
    name: r.name,
    base2hPrice: Number(r.base2hPrice),
    hour3Supplement: Number(r.hour3Supplement),
    hour4plusUnit: Number(r.hour4plusUnit),
    overnightPrice: Number(r.overnightPrice),
    weekendMaxHours: r.weekendMaxHours,
    weekendSurchargePct: r.weekendSurchargePct,
    holidayMaxHours: r.holidayMaxHours,
    holidaySurchargePct: r.holidaySurchargePct,
  };
}

// --------------------------- RoomInfoCard ---------------------------

function RoomInfoCard({
  code,
  building,
  floor,
  tipoLabel,
  listinoName,
}: {
  code: string;
  building: string;
  floor: string;
  tipoLabel: string;
  listinoName: string;
}) {
  return (
    <article
      data-testid="room-info-card"
      className="rounded-xl border border-accent-soft p-4 flex items-center gap-3.5"
      style={{ background: '#FCF1EA' }}
    >
      <span className="w-12 h-12 rounded-[10px] bg-bg-card flex items-center justify-center">
        <CircleCheck size={24} className="text-accent-primary" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[22px] font-bold text-text-primary flex items-center gap-2.5">
          Camera {code}
          <span className="bg-bg-card text-accent-primary-text px-2.5 py-[3px] rounded-md text-[11px] font-semibold border border-accent-soft uppercase tracking-[0.4px]">
            {tipoLabel}
          </span>
        </div>
        <div className="text-[13px] text-text-secondary mt-0.5">
          {buildingLabelOf(building)} · Piano {floor} · Listino "{listinoName}"
        </div>
      </div>
      <span className="bg-bg-card text-[#5F8A50] px-2.5 py-1 rounded-md text-[11px] font-semibold border border-[#C5DBB8]">
        LIBERA
      </span>
    </article>
  );
}

// --------------------------- SectionTitleRow ---------------------------

function SectionTitleRow({ title }: { title: string }) {
  return (
    <div className="flex justify-between items-center mb-3">
      <h3 className="text-[13px] font-bold text-text-primary flex items-center gap-2">
        <CalendarClock size={17} className="text-accent-primary" />
        {title}
      </h3>
    </div>
  );
}

// --------------------------- DurationPresets ---------------------------

function DurationPresets({
  selected,
  presetHours,
  presetCalcs,
  overnightPrice,
  checkInDate,
  onSelect,
}: {
  selected: number | 'overnight';
  presetHours: readonly number[];
  presetCalcs: Map<number, PriceCalcResult>;
  overnightPrice: number;
  checkInDate: Date;
  onSelect: (value: number | 'overnight') => void;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="duration-presets">
      {presetHours.map((h) => {
        const c = presetCalcs.get(h);
        const checkout = new Date(checkInDate.getTime() + h * 60 * 60 * 1000);
        return (
          <PresetCard
            key={h}
            selected={selected === h}
            onClick={() => onSelect(h)}
            label="Day Use"
            name={`${h} ore`}
            price={c ? `€ ${c.total.toFixed(0)}` : '…'}
            time={`${formatTime(checkInDate)} → ${formatTime(checkout)}`}
            testid={`preset-${h}h`}
          />
        );
      })}
      <PresetCard
        selected={selected === 'overnight'}
        onClick={() => onSelect('overnight')}
        label="Pernotto"
        name="Notte"
        price={`€ ${overnightPrice.toFixed(0)}`}
        time={`${formatTime(checkInDate)} → 11:00`}
        testid="preset-overnight"
        moon
      />
    </div>
  );
}

function PresetCard({
  selected,
  onClick,
  label,
  name,
  price,
  time,
  testid,
  moon,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
  name: string;
  price: string;
  time: string;
  testid: string;
  moon?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-selected={selected}
      className={cn(
        'bg-bg-card border-2 border-border-base rounded-xl p-3 text-center transition-all',
        'hover:border-border-input hover:-translate-y-px',
        selected &&
          '!border-accent-primary bg-accent-soft shadow-[0_4px_12px_rgba(233,123,61,0.15)]',
      )}
    >
      <div className="mb-1.5">
        {moon ? (
          <Wallet size={18} className="text-accent-primary mx-auto" />
        ) : (
          <CalendarClock size={18} className="text-accent-primary mx-auto" />
        )}
      </div>
      <div className="text-[11px] uppercase font-semibold text-text-secondary tracking-[0.4px]">
        {label}
      </div>
      <div className="text-[17px] font-bold text-text-primary mt-0.5">{name}</div>
      <div className="text-[18px] font-bold text-accent-primary mt-1.5 tracking-[-0.02em]">
        {price}
      </div>
      <div className="text-[10px] text-text-secondary mt-1">{time}</div>
    </button>
  );
}

// --------------------------- CustomDurationSection ---------------------------

function CustomDurationSection({
  hours,
  isOvernight,
  bufferMinutes,
  checkInDate,
  onChange,
}: {
  hours: number;
  isOvernight: boolean;
  bufferMinutes: number;
  checkInDate: Date;
  onChange: (h: number) => void;
}) {
  const dec = useCallback(() => {
    if (hours > 1) onChange(hours - 1);
  }, [hours, onChange]);
  const inc = useCallback(() => {
    if (hours < 24) onChange(hours + 1);
  }, [hours, onChange]);
  const checkout = isOvernight ? null : new Date(checkInDate.getTime() + hours * 60 * 60 * 1000);

  return (
    <section className="bg-bg-base border border-border-base rounded-xl p-4">
      <div className="mb-3.5">
        <div className="text-xs font-bold text-text-primary">Durata personalizzata</div>
        <div className="text-[11px] text-text-secondary mt-0.5">
          Aggiungi ore extra rispetto ai preset
        </div>
      </div>
      <div className="bg-bg-card border border-border-input rounded-[10px] p-3 flex items-center gap-3.5">
        <div className="flex-1">
          <div className="text-[11px] uppercase font-semibold text-text-secondary tracking-[0.4px]">
            Ore totali soggiorno
          </div>
          <div className="text-[22px] font-bold text-text-primary mt-0.5 tracking-[-0.02em]">
            {isOvernight ? 'Pernottamento' : `${hours} ${hours === 1 ? 'ora' : 'ore'}`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CounterBtn onClick={dec} disabled={isOvernight || hours <= 1} testid="counter-dec">
            −
          </CounterBtn>
          <div
            className="bg-bg-card border-[1.5px] border-border-input rounded-lg px-3.5 py-1.5 text-lg font-bold text-text-primary min-w-[56px] text-center"
            data-testid="counter-display"
          >
            {isOvernight ? '🌙' : hours}
          </div>
          <CounterBtn onClick={inc} disabled={isOvernight || hours >= 24} testid="counter-inc">
            +
          </CounterBtn>
        </div>
      </div>

      <TimePreview
        checkIn={checkInDate}
        checkOut={checkout}
        bufferMinutes={bufferMinutes}
        isOvernight={isOvernight}
      />
    </section>
  );
}

function CounterBtn({
  onClick,
  disabled,
  children,
  testid,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  testid?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={cn(
        'w-9 h-9 rounded-lg bg-bg-card border-[1.5px] border-border-base text-text-primary font-bold flex items-center justify-center transition-colors',
        'hover:border-accent-primary hover:text-accent-primary hover:bg-accent-soft',
        'disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-border-base disabled:hover:text-text-primary disabled:hover:bg-bg-card',
      )}
    >
      {children}
    </button>
  );
}

function TimePreview({
  checkIn,
  checkOut,
  bufferMinutes,
  isOvernight,
}: {
  checkIn: Date;
  checkOut: Date | null;
  bufferMinutes: number;
  isOvernight: boolean;
}) {
  return (
    <div
      className="bg-bg-card border border-border-base rounded-[10px] p-3.5 flex items-center justify-around mt-3.5"
      data-testid="time-preview"
    >
      <TimeBlock label="Check-in" value={formatTime(checkIn)} />
      <span className="text-accent-primary text-lg">→</span>
      <TimeBlock
        label="Check-out previsto"
        value={isOvernight ? '11:00' : checkOut ? formatTime(checkOut) : '—'}
      />
      <span className="text-accent-primary text-lg">+</span>
      <TimeBlock label="Cuscinetto" value={`${bufferMinutes} min`} />
    </div>
  );
}

function TimeBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center">
      <div className="text-[10px] text-text-muted uppercase tracking-[0.6px] font-semibold">
        {label}
      </div>
      <div className="text-xl font-bold text-text-primary mt-1 tabular-nums">{value}</div>
    </div>
  );
}

// --------------------------- PriceBreakdown ---------------------------

function PriceBreakdown({
  listinoName,
  calc,
}: {
  listinoName: string;
  calc: (PriceCalcResult & { isHoliday: boolean; isWeekend: boolean }) | null;
}) {
  if (!calc) {
    return (
      <div
        className="rounded-xl border border-[#C5DBB8] p-5"
        style={{ background: 'linear-gradient(135deg, #F4FAF1 0%, #FAF7F2 100%)' }}
      >
        <p className="text-sm text-text-muted">Calcolo prezzo in corso…</p>
      </div>
    );
  }

  const dayLabel = new Date().toLocaleDateString('it-IT', { weekday: 'long' });
  return (
    <div
      data-testid="price-breakdown"
      className="rounded-xl border border-[#C5DBB8] p-5"
      style={{ background: 'linear-gradient(135deg, #F4FAF1 0%, #FAF7F2 100%)' }}
    >
      <div className="text-[11px] font-bold text-[#5F8A50] uppercase tracking-[0.6px] mb-3">
        Calcolo prezzo (listino {listinoName} · {dayLabel})
      </div>
      {calc.breakdown.map((row, idx) => (
        <div
          key={`${row.label}-${idx}`}
          className="flex justify-between py-1.5 text-[13px] text-text-secondary border-b border-dashed border-[#C5DBB8] last:border-none"
        >
          <span>{row.label}</span>
          <span className="text-text-primary font-bold">€ {row.amount.toFixed(2)}</span>
        </div>
      ))}
      <div className="flex justify-between pt-3 mt-2 border-t border-[#C5DBB8] text-[17px] font-bold text-text-primary">
        <span>Totale</span>
        <span
          className="text-accent-primary-text tabular-nums tracking-[-0.02em] text-[22px]"
          data-testid="price-breakdown-total"
        >
          € {calc.total.toFixed(2)}
        </span>
      </div>
    </div>
  );
}

// --------------------------- WarningBanner ---------------------------

function WarningBanner({ title, message }: { title: string; message: string }) {
  return (
    <div
      data-testid="warning-banner"
      className="rounded-lg border p-3 text-xs flex items-start gap-2.5"
      style={{ background: '#FFF8EC', borderColor: '#F5C77D', color: '#8B5A1F' }}
    >
      <span className="text-base shrink-0">⚠</span>
      <div>
        <strong className="text-text-primary">{title}</strong> {message}
      </div>
    </div>
  );
}

// --------------------------- StepFooter ---------------------------

function StepFooter() {
  const setStep = useWalkInWizard((s) => s.setStep);
  const close = useWalkInWizard((s) => s.close);
  const dynamicPricing = useWalkInWizard((s) => s.dynamicPricing);

  return (
    <footer className="flex items-center justify-between pt-3 border-t border-border-base">
      <div className="text-xs text-text-secondary">Step 1 di 4 · Camera</div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={close}
          className="bg-bg-card text-text-secondary border border-border-base px-5 py-2.5 rounded-lg text-[13px] font-semibold"
          data-testid="walkin-cancel"
        >
          Annulla
        </button>
        <button
          type="button"
          onClick={() => setStep(2)}
          disabled={!dynamicPricing.priceCalc}
          data-testid="walkin-next-1"
          className={cn(
            'bg-accent-primary text-text-on-accent border-0 px-7 py-2.5 rounded-lg text-[13px] font-semibold flex items-center gap-1.5 transition-all',
            'hover:bg-accent-primary-hover',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
          style={{ boxShadow: '0 2px 8px rgba(233, 123, 61, 0.25)' }}
        >
          Avanti →
        </button>
      </div>
    </footer>
  );
}

// --------------------------- helpers ---------------------------

function formatTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
